/**
 * Hook to resolve the effective tenant ID for React Query key scoping.
 *
 * For admin users with act-as-tenant, returns the selected tenant ID.
 * For regular users, returns their own tenant ID from the auth context.
 *
 * @returns The effective tenant ID (number) used to scope all query keys.
 *
 * @example
 * ```tsx
 * const tenantId = useTenantId();
 * const { data } = useQuery({
 *   queryKey: queryKeys.cases.all(tenantId),
 *   queryFn: () => casesApi.getAll(),
 * });
 * ```
 */

import { useAuth } from "../contexts/AuthContext";
import { useTenant } from "../contexts/TenantContext";

export function useTenantId(): number {
  const { user } = useAuth();
  const { selectedTenantId } = useTenant();

  // Admin act-as-tenant takes precedence, then fall back to the user's own tenant
  return selectedTenantId ?? user?.tenantId ?? 0;
}
